/**
 * Edition registry — the single source of truth for which tournaments
 * the site knows about and which one is "current".
 *
 * Ordering: current edition first, then archived editions in
 * reverse-chronological order. The prerender produces year-prefixed
 * pages for every edition in this list, so older editions stay live as
 * permanent SEO archives indefinitely.
 *
 * Adding a new edition (e.g. 2030):
 *   1. Create `editions/2030.ts` with the 48-team finals roster.
 *   2. Import it below and prepend it to `EDITIONS`.
 *   3. Run `npm run build` to verify the new pages prerender.
 *   4. Ship. The new-season cron will pick up the DB-side rollover
 *      (subscription expiry, fixture sync, email) when football-data.org
 *      starts returning fixtures for the new season.
 */

import type { Edition } from "./types";
import { edition2026 } from "./2026";

/** All known editions, current first. */
export const EDITIONS: ReadonlyArray<Edition> = [
  edition2026,
];

/** The current edition (most recent). */
export const CURRENT_EDITION: Edition = EDITIONS[0];

/** Convenience: just the years, current first. */
export const ALL_TOURNAMENT_YEARS: ReadonlyArray<number> = EDITIONS.map((e) => e.year);

/** Convenience: current tournament year (matches the historical `TOURNAMENT_YEAR` constant). */
export const TOURNAMENT_YEAR = CURRENT_EDITION.year;

/** Look up an edition by year. Throws if the year is unknown. */
export function getEdition(year: number): Edition {
  const edition = EDITIONS.find((e) => e.year === year);
  if (!edition) throw new Error(`Unknown edition year: ${year}`);
  return edition;
}

/** Get all 48 teams in the current edition (the historical `WC2026_TEAMS` shape). */
export const CURRENT_TEAMS = CURRENT_EDITION.teams;

export type { Edition, EditionTeam } from "./types";
